// src/pages/reports/ReportsPage.tsx

import React, { useState, useEffect, useMemo } from "react";
import {
  Box,
  Card,
  CircularProgress,
  Container,
  Typography,
} from "@mui/material";
import { useUserStore } from "@/hooks/useUserStore";
import DataTable, { createDataRow, HeadCell } from "@/components/DataTable";

const { QlikEmbed } = require("@qlik/embed-react");

type HyperCubeDataPage = {
  qMatrix: Array<Array<{ qText: string; qNum?: number }>>;
  qLeft: number;
  qTop: number;
  qHeight: number;
  qWidth: number;
};

const ReportsPage: React.FC = () => {
  const { userInfo } = useUserStore();
  const APP_ID = process.env.REACT_APP_QLIK_APP_ID || "";
  const TABLE_OBJECT_ID = "QjJSkER";

  const [qlikApi, setQlikApi] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [tableData, setTableData] = useState<Array<
    Array<{ qText: string; qNum?: number }>
  > | null>(null);

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  useEffect(() => {
    if (!qlikApi) return;
    let cancelled = false;

    qlikApi
      .getDoc()
      .then(({ app }: any) => app.getObject(TABLE_OBJECT_ID))
      .then((model: any) => {
        if (cancelled) return;
        return model.getLayout().then((layout: any) => ({ model, layout }));
      })
      .then(({ model, layout }: { model: any; layout: any }) => {
        if (cancelled) return;

        const totalRows = layout.qHyperCube.qSize.qcy;
        const totalCols = layout.qHyperCube.qSize.qcx;
        const pageSize = 1000;
        const numPages = Math.ceil(totalRows / pageSize);

        const promises: Array<Promise<HyperCubeDataPage>> = [];
        for (let i = 0; i < numPages; i++) {
          const qTop = i * pageSize;
          const qHeight = Math.min(pageSize, totalRows - qTop);

          promises.push(
            model
              .getHyperCubeData({
                qPath: "/qHyperCubeDef",
                qPages: [
                  {
                    qLeft: 0,
                    qTop,
                    qWidth: totalCols,
                    qHeight,
                  },
                ],
              })
              .then((pages: HyperCubeDataPage[]) => pages[0]),
          );
        }

        return Promise.all(promises);
      })
      .then((allPages: HyperCubeDataPage[]) => {
        if (cancelled) return;

        const allRows: Array<Array<{ qText: string; qNum?: number }>> = [];
        allPages.forEach((pageData) => {
          pageData.qMatrix.forEach((row) => allRows.push(row));
        });

        setTableData(allRows);
        setIsLoading(false);
      })
      .catch((err: any) => {
        console.error(err);
        if (!cancelled) setIsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [qlikApi]);

  const { headCells, rows } = useMemo(() => {
    if (!tableData || tableData.length === 0) {
      return { headCells: [] as HeadCell[], rows: [] };
    }

    const numCols = tableData[0].length;
    const generatedHeadCells: HeadCell[] = [];
    for (let colIndex = 0; colIndex < numCols; colIndex++) {
      generatedHeadCells.push({
        id: `col${colIndex}`,
        isSortable: false,
        disablePadding: false,
        label: `Column ${colIndex + 1}`,
        align: "left",
        width: "150px",
      });
    }

    const generatedRows = tableData.map((rowArray, rowIndex) => {
      const record: Record<string, React.ReactNode> = {};
      rowArray.forEach((cell, colIndex) => {
        record[`col${colIndex}`] = <span>{cell.qText}</span>;
      });
      return createDataRow(String(rowIndex), record);
    });

    return { headCells: generatedHeadCells, rows: generatedRows };
  }, [tableData]);

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const paginatedRows = rows.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage,
  );

  return (
    <Container>
      <Typography variant="h4" gutterBottom>
        Reports
      </Typography>
      <Box
        sx={{
          position: "absolute",
          width: 0,
          height: 0,
          overflow: "hidden",
          bgcolor: "background.paper",
          borderRadius: 1,
          boxShadow: 1,
          opacity: 0,
        }}
      >
        {isLoading && (
          <Box
            sx={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              zIndex: 1,
              pointerEvents: "none",
            }}
          >
            <CircularProgress size={32} />
          </Box>
        )}

        <Box
          sx={{
            position: "relative",
            zIndex: 2,
            width: "100%",
            height: "100%",
          }}
        >
          <QlikEmbed
            ui="analytics/chart"
            appId={APP_ID}
            objectId={TABLE_OBJECT_ID}
            identity={userInfo?._id}
            style={{ width: "100%", height: "100%" }}
            exposeRefApi={(ref: any) => {
              if (!qlikApi && ref) {
                setQlikApi(ref);
              }
            }}
          />
        </Box>
      </Box>
      {!isLoading && headCells.length > 0 && (
        <Box sx={{ mt: 4 }}>
          <Card sx={{ borderRadius: 1, boxShadow: 1 }}>
            <Box sx={{ p: 2 }}>
              <Typography variant="h6" gutterBottom>
                Underlying Qlik Table Data
              </Typography>

              <Box sx={{ overflowX: "auto" }}>
                <Box component="div">
                  <DataTable
                    data={paginatedRows}
                    headCells={headCells}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    total={rows.length}
                    isLoading={false}
                    order="asc"
                    orderBy={headCells[0]?.id || ""}
                    onChangePage={handleChangePage}
                    onChangeSize={handleChangeRowsPerPage}
                    onChangeSort={() => {}}
                  />
                </Box>
              </Box>
            </Box>
          </Card>
        </Box>
      )}
    </Container>
  );
};

export default ReportsPage;
